import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  FiUpload,
  FiSend,
  FiFile,
  FiLoader,
  FiX,
  FiDownload,
} from "react-icons/fi";
import {
  BsRobot,
  BsPerson,
  BsArrowLeft,
  BsFileEarmarkText,
} from "react-icons/bs";
import { MdOutlineAttachFile } from "react-icons/md";
import jsPDF from "jspdf";
import { getApiUrl } from "./config";
import "./App.css";
import HomePage from "./HomePage";
import { BrowserRouter as Router, Route, Routes, Link } from "react-router-dom";

function App() {
  const [file, setFile] = useState(null);
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState({
    upload: false,
    question: false,
    report: false,
  });
  const [pdfUploaded, setPdfUploaded] = useState(false);
  const [reportGenerated, setReportGenerated] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportProgress, setReportProgress] = useState("");
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleUpload = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      setLoading((prev) => ({ ...prev, upload: true }));
      await axios.post(`${getApiUrl()}/upload`, formData);
      setPdfUploaded(true);
      addMessage("PDF uploaded and ready for questions!", "system");
    } catch (err) {
      addMessage("Failed to upload PDF. Please try again.", "error");
    } finally {
      setLoading((prev) => ({ ...prev, upload: false }));
    }
  };
  const generateReport = async () => {
    if (!pdfUploaded) return;

    setLoading((prev) => ({ ...prev, report: true }));
    setReportProgress("Starting comprehensive report generation...");

    try {
      // Enhanced comprehensive question including all three sections
      const comprehensiveQuestion = `Please analyze this financial document and provide information in the exact format below:

SECTION 1 - COMPANY INFORMATION (Provide detailed paragraphs for each):
Company Name: [Provide the full legal name and reporting period with any additional context]
Main Activities: [Provide a detailed explanation of what the business does, its core operations, services offered, and business model]
Brands: [Detail the number of brands the dealership deals with, name them if listed, and explain their significance to the business]
Directors: [List all current directors with their roles, mention any changes during the year, and provide context about leadership]
Market Conditions: [Provide a comprehensive description of market challenges, opportunities, economic factors, and industry conditions mentioned in the report]
Business Overview: [Give a detailed overview of how the business performed during the year, key achievements, challenges faced, strategic initiatives, and future opportunities]
Exceptions: [Explain any exceptions reported in the financial statements, their nature, and potential impact]
Auditor's Opinion: [Detail whether the auditors gave a qualified or clean opinion, and explain what this means for the business]
Going Concern: [Explain if there is any risk of the business not continuing, factors affecting going concern, and management's assessment]

SECTION 2 - FINANCIAL DATA (Provide ONLY the final amounts in this exact format):
Current Year Turnover: £XXX,XXX
Current Year Cost of Sales: £XXX,XXX  
Current Year Gross Profit: £XXX,XXX
Current Year Operating Expenses: £XXX,XXX
Current Year Operating Profit: £XXX,XXX (use parentheses for losses: (£XXX,XXX))
Current Year Finance Costs: £XXX,XXX
Current Year Profit After Tax: £XXX,XXX (use parentheses for losses: (£XXX,XXX))

Previous Year Turnover: £XXX,XXX
Previous Year Cost of Sales: £XXX,XXX
Previous Year Gross Profit: £XXX,XXX
Previous Year Operating Expenses: £XXX,XXX
Previous Year Operating Profit: £XXX,XXX (use parentheses for losses: (£XXX,XXX))
Previous Year Finance Costs: £XXX,XXX
Previous Year Profit After Tax: £XXX,XXX (use parentheses for losses: (£XXX,XXX))

SECTION 3 - FINANCIAL ANALYSIS OF CONSOLIDATED FINANCIAL POSITION STATEMENT (Provide ONLY the final amounts in this exact format. PRIORITISE the Consolidated/Group column from the Statement of Financial Position. Only use the Company/Parent column if the Group data is absent, and clearly mark unavailable figures as "N/A"):
Current Year Fixed Assets:
Current Year Total Stocks:
Current Year Total Debtors:
Current Year Total Cash:
Current Year Total Assets (Fixed Assets + Total Current Assets):
Current Year Trade Creditors:
Current Year Consignment Stock Creditors:
Current Year Amounts Owed to Group Undertakings:
  - IMPORTANT: For "Amounts owed by group undertakings", search the notes to the accounts and related party disclosures. Only use the Company column if the Group column is missing. If the value is zero, state "£0". If not present, state "N/A".
Current Year Directors' Loan Accounts:
Current Year Overdraft:
Current Year Loans:
Current Year Current Liabilities:
  - IMPORTANT: Look for "Creditors: amounts falling due within one year" OR "Current liabilities" OR "Trade and other payables" in the Balance Sheet. This includes trade creditors, accruals, short-term borrowings, and other payables due within 12 months.
Current Year Total Long-Term Liability:
  - IMPORTANT: Look for "Creditors: amounts falling due after more than one year" OR "Non-current liabilities" OR "Provisions for liabilities and charges" OR "Long-term borrowings" in the Balance Sheet. This includes long-term loans, deferred tax, pension provisions, and other long-term obligations.
Current Year Total Liabilities:
  - IMPORTANT: This should be Current Liabilities + Total Long-Term Liability. If not explicitly stated, calculate by adding the two components above. Look for "Total liabilities" in the Balance Sheet.
Current Year Net Assets / Shareholders' Funds:

Previous Year Fixed Assets:
Previous Year Total Stocks:
Previous Year Total Debtors:
Previous Year Total Cash:
Previous Year Total Assets (Fixed Assets + Total Current Assets):
Previous Year Trade Creditors:
Previous Year Consignment Stock Creditors:
Previous Year Amounts Owed to Group Undertakings:
  - IMPORTANT: For "Amounts owed by group undertakings", search the notes to the accounts and related party disclosures. Only use the Company column if the Group column is missing. If the value is zero, state "£0". If not present, state "N/A".
Previous Year Directors' Loan Accounts: 
Previous Year Overdraft:
Previous Year Loans:
Previous Year Current Liabilities:
  - IMPORTANT: Look for "Creditors: amounts falling due within one year" OR "Current liabilities" OR "Trade and other payables" in the Balance Sheet. This includes trade creditors, accruals, short-term borrowings, and other payables due within 12 months.
Previous Year Total Long-Term Liability:
  - IMPORTANT: Look for "Creditors: amounts falling due after more than one year" OR "Non-current liabilities" OR "Provisions for liabilities and charges" OR "Long-term borrowings" in the Balance Sheet. This includes long-term loans, deferred tax, pension provisions, and other long-term obligations.
Previous Year Total Liabilities:
  - IMPORTANT: This should be Current Liabilities + Total Long-Term Liability. If not explicitly stated, calculate by adding the two components above. Look for "Total liabilities" in the Balance Sheet.
Previous Year Net Assets / Shareholders' Funds:

SOURCE PRIORITY AND DATA SELECTION RULES:
1. Always locate the Statement of Financial Position / Balance Sheet section before looking elsewhere.
2. Use the Consolidated/Group column first. Only fall back to the Company/Parent column when the Group column is missing, and state "N/A" if neither exists.
3. Confirm that "Amounts owed to group undertakings" comes from the Group column or note disclosure referring to the Group.
4. For liabilities, look for these common UK balance sheet terms:
   - Current liabilities: "Creditors: amounts falling due within one year" or "Current liabilities"
   - Long-term liabilities: "Creditors: amounts falling due after more than one year" or "Non-current liabilities"
   - Total liabilities: Sum of current + long-term, or look for "Total liabilities" line
5. If exact liability breakdowns are not visible, extract from subtotals or notes to the accounts.

CRITICAL FORMATTING RULES:
1. For losses or negative amounts, use parentheses: (£XXX,XXX)
2. For calculations, provide ONLY the final calculated amount, NOT the calculation steps or intermediate values
3. Ensure all amounts are complete with proper digits
4. Do not add explanations, calculation notes, or text after the amounts in the financial data section
5. If you need to calculate operating expenses by adding multiple items, show ONLY the final total
6. NEVER show formats like "£8,205,456 *(£2,913,419 + £5,272,035 - £13,050)*" - this is WRONG
7. CORRECT format: If calculation gives £8,192,404, show ONLY: £8,192,404
8. Double-check all arithmetic: £2,913,419 + £5,272,035 - £13,050 = £8,192,404 (not £8,205,456)
9. Total Assets must equal Fixed Assets + Total Current Assets (stocks + debtors + cash). If any component is missing, return "N/A" instead of inferring.
10. Total Liabilities must equal Current Liabilities/Creditors + Total Long-Term Liability (Provisions for liabilities / Non-current liabilities). If either input is missing, return "N/A".
11. When a figure is zero, state "£0" explicitly.

CALCULATION VERIFICATION EXAMPLES:
- Current Year Operating Expenses: Calculate £2,913,419 + £5,272,035 - £13,050 = £8,192,404
- Previous Year Operating Expenses: Calculate £2,876,458 + £2,960,843 - £9,899 = £5,827,402

DO NOT show calculation steps in the financial data sections. Show ONLY final amounts.

DETAILED FINANCIAL EXPLANATION:
Paragraph 1: [Explain the specific reasons for each increase or decrease in the financial figures above, using details and context from the financial statements, director's report, and notes to the accounts]

Paragraph 2: [Provide external market analysis explaining broader industry trends, economic factors, market news, and external influences that may have contributed to the company's financial performance]

DETAILED BALANCE SHEET EXPLANATION:
Balance Sheet Analysis: [The consolidated balance sheet shows total assets of £[Total Assets CY] versus £[Total Assets PY], with net assets/shareholders' funds of £[Net Assets CY] compared to £[Net Assets PY]. Fixed assets changed from £[FA PY] to £[FA CY] due to [capital expenditure / disposals / depreciation]. Stocks moved from £[Stocks PY] to £[Stocks CY], reflecting [inventory build or reduction]. Debtors changed from £[Debtors PY] to £[Debtors CY], mainly because of [credit-control or sales changes]. Cash balances shifted from £[Cash PY] to £[Cash CY] as a result of [operating cash flow, financing, or investment]. On the liability side, total liabilities moved from £[Liabilities PY] to £[Liabilities CY], driven by [loans repaid/raised, creditor timing]. Overall these movements indicate [brief conclusion—e.g., a stronger/weaker liquidity position].]

CONCLUSION & RECOMMENDATIONS:
Conclusion: [Write a comprehensive, detailed, and analytical conclusion. Go beyond a summary—explain the overall financial health, trends, and risks [Low / Medium / High], referencing specific figures and changes from the profit & loss and balance sheet sections. Discuss the implications of profitability, cash flow, liquidity, gearing, and any external market or industry factors. Clearly justify the risk classification and provide a nuanced rationale, including both strengths and weaknesses. Use at least 5-6 sentences.]

Recommendations: [Provide 3-5 specific, actionable recommendations. For each, explain the reasoning, expected impact, and how it addresses the identified issues or opportunities. Recommendations should be tailored to the business's financial situation, market context, and strategic goals.]

Please provide comprehensive, detailed responses for each section. For financial data, use only final amounts with proper formatting for negative values.`;

      setReportProgress(
        "Processing all financial data including balance sheet analysis..."
      );

      const res = await axios.post(`${getApiUrl()}/ask`, {
        question: comprehensiveQuestion,
      });

      // Parse the enhanced comprehensive response
      const response = res.data.answer;
      console.log("AI Response:", response); // Debug log to see what AI returns
      const reportData = parseComprehensiveResponse(response);

      // Validate calculations in the response
      validateCalculations(response);

      setReportData(reportData);
      setReportGenerated(true);
      setReportProgress("Comprehensive report generated successfully!");
      addMessage(
        "Complete report with balance sheet analysis has been generated! You can now download it as a PDF.",
        "system"
      );
    } catch (err) {
      addMessage("Failed to generate report. Please try again.", "error");
      setReportProgress("");
    } finally {
      setLoading((prev) => ({ ...prev, report: false }));
    }
  };

  // Helper function to validate calculations in AI response
  const validateCalculations = (response) => {
    console.log(`\n🔍 === VALIDATION STARTING ===`);

    // Check for incorrect format patterns (values with calculation notes)
    const incorrectFormatPattern = /£([0-9,]+(?:\.[0-9]+)?)\s*\*\([^)]+\)\*/g;
    let formatIssues = [];
    let match;

    while ((match = incorrectFormatPattern.exec(response)) !== null) {
      formatIssues.push({
        fullMatch: match[0],
        wrongValue: match[1],
        context: response.substring(
          Math.max(0, match.index - 50),
          match.index + match[0].length + 50
        ),
      });
    }

    if (formatIssues.length > 0) {
      console.warn(`⚠️ INCORRECT FORMAT DETECTED!`);
      formatIssues.forEach((issue, index) => {
        console.warn(`Issue ${index + 1}: Found "${issue.fullMatch}"`);
        console.warn(`Wrong value being shown: £${issue.wrongValue}`);
        console.warn(`Context: ${issue.context}`);
      });

      addMessage(
        `⚠️ AI response format issue detected. The system will correct calculations automatically, but please verify the final numbers.`,
        "warning"
      );
    }

    // Find all calculation expressions and validate them
    const calculationPattern =
      /([£]?[0-9,]+(?:\.[0-9]+)?)\s*\+\s*([£]?[0-9,]+(?:\.[0-9]+)?)\s*-\s*([£]?[0-9,]+(?:\.[0-9]+)?)\s*=\s*([£]?[0-9,]+(?:\.[0-9]+)?)/g;

    let calcMatch;
    while ((calcMatch = calculationPattern.exec(response)) !== null) {
      const [fullMatch, num1, num2, num3, result] = calcMatch;

      // Clean and parse numbers
      const parseAmount = (val) =>
        parseFloat(val.toString().replace(/[£,]/g, ""));

      const a = parseAmount(num1);
      const b = parseAmount(num2);
      const c = parseAmount(num3);
      const expected = a + b - c;
      const actual = parseAmount(result);

      console.log(`\n🧮 === Calculation Validation ===`);
      console.log(`Found: ${fullMatch}`);
      console.log(`Calculation: ${a} + ${b} - ${c} = ${expected}`);
      console.log(`AI provided: ${actual}`);

      if (Math.abs(expected - actual) > 0.01) {
        // Allow for small rounding differences
        console.warn(`❌ CALCULATION ERROR DETECTED!`);
        console.warn(`Expected: £${expected.toLocaleString()}`);
        console.warn(`AI gave: £${actual.toLocaleString()}`);
        console.warn(
          `Difference: £${Math.abs(expected - actual).toLocaleString()}`
        );

        addMessage(
          `❌ Calculation error detected: Expected £${expected.toLocaleString()} but AI provided £${actual.toLocaleString()}. Frontend will use correct calculation.`,
          "warning"
        );
      } else {
        console.log(`✅ Calculation is correct`);
      }
    }

    console.log(`🔍 === VALIDATION COMPLETE ===\n`);
  };

  // Enhanced helper function to parse the comprehensive response including balance sheet data
  const parseComprehensiveResponse = (response) => {
    const data = {
      companyInfo: {},
      currentYear: {},
      previousYear: {},
      balanceSheet: {
        currentYear: {},
        previousYear: {},
      },
      balanceSheetAnalysis: "",
      conclusion: "",
      recommendations: "",
      rawResponse: response,
    };

    // Helper function to clean markdown formatting
    const cleanMarkdown = (text) => {
      if (!text) return "N/A";
      return text
        .replace(/\*\*/g, "") // Remove ** markdown bold
        .replace(/\*/g, "") // Remove * markdown italic
        .trim();
    };

    const parseCurrencyToNumber = (value) => {
      if (!value || value === "N/A") return null;
      const str = value.toString().trim();
      if (!str) return null;

      const isNegative =
        (str.includes("(") && str.includes(")")) || str.startsWith("-");

      const numericPart = str
        .replace(/[£,()\s]/g, "")
        .replace(/^-/g, "")
        .trim();

      if (!numericPart) return null;

      const parsed = parseFloat(numericPart);
      if (Number.isNaN(parsed)) return null;

      return isNegative ? -parsed : parsed;
    };

    const formatCurrency = (value) => {
      if (value === null || value === undefined) return "N/A";
      const absValue = Math.abs(value);
      const formatted = absValue.toLocaleString(undefined, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
      });
      return value < 0 ? `(£${formatted})` : `£${formatted}`;
    };

    // Extract company information
    const companyName = response.match(/Company Name:\s*(.+)/i);
    const mainActivities = response.match(/Main Activities:\s*(.+)/i);
    const brands = response.match(/Brands:\s*(.+)/i);
    const directors = response.match(/Directors:\s*(.+)/i);
    const marketConditions = response.match(/Market Conditions:\s*(.+)/i);
    const businessOverview = response.match(/Business Overview:\s*(.+)/i);
    const exceptions = response.match(/Exceptions:\s*(.+)/i);
    const auditorOpinion = response.match(/Auditor's Opinion:\s*(.+)/i);
    const goingConcern = response.match(/Going Concern:\s*(.+)/i);

    data.companyInfo.companyName = cleanMarkdown(
      companyName ? companyName[1] : null
    );
    data.companyInfo.mainActivities = cleanMarkdown(
      mainActivities ? mainActivities[1] : null
    );
    data.companyInfo.brands = cleanMarkdown(brands ? brands[1] : null);
    data.companyInfo.directors = cleanMarkdown(directors ? directors[1] : null);
    data.companyInfo.marketConditions = cleanMarkdown(
      marketConditions ? marketConditions[1] : null
    );
    data.companyInfo.businessOverview = cleanMarkdown(
      businessOverview ? businessOverview[1] : null
    );
    data.companyInfo.exceptions = cleanMarkdown(
      exceptions ? exceptions[1] : null
    );
    data.companyInfo.auditorOpinion = cleanMarkdown(
      auditorOpinion ? auditorOpinion[1] : null
    );
    data.companyInfo.goingConcern = cleanMarkdown(
      goingConcern ? goingConcern[1] : null
    );

    // Extract detailed financial explanations with markdown formatting
    const paragraph1 = response.match(
      /\*\*Paragraph 1:\*\*\s*([\s\S]*?)(?=\*\*Paragraph 2:\*\*|$)/i
    );
    const paragraph2 = response.match(
      /\*\*Paragraph 2:\*\*\s*([\s\S]*?)(?=\*\*Balance Sheet Analysis:\*\*|$)/i
    );

    data.financialExplanation = {
      paragraph1: cleanMarkdown(paragraph1 ? paragraph1[1] : null),
      paragraph2: cleanMarkdown(paragraph2 ? paragraph2[1] : null),
    };

    // Extract balance sheet analysis with multiple patterns
    let balanceSheetAnalysis = response.match(
      /\*\*Balance Sheet Analysis:\*\*\s*([\s\S]*?)(?=\*\*Conclusion:\*\*|\*\*CONCLUSION:|$)/i
    );
    if (!balanceSheetAnalysis) {
      balanceSheetAnalysis = response.match(
        /Balance Sheet Analysis:\s*([\s\S]*?)(?=Conclusion:|CONCLUSION:|$)/i
      );
    }
    if (!balanceSheetAnalysis) {
      balanceSheetAnalysis = response.match(
        /DETAILED BALANCE SHEET EXPLANATION:\s*([\s\S]*?)(?=CONCLUSION|Conclusion:|$)/i
      );
    }
    data.balanceSheetAnalysis = cleanMarkdown(
      balanceSheetAnalysis ? balanceSheetAnalysis[1] : null
    );

    // Extract conclusion with multiple patterns
    let conclusion = response.match(
      /\*\*Conclusion:\*\*\s*([\s\S]*?)(?=\*\*Recommendations:\*\*|\*\*RECOMMENDATIONS:|$)/i
    );
    if (!conclusion) {
      conclusion = response.match(
        /Conclusion:\s*([\s\S]*?)(?=Recommendations:|RECOMMENDATIONS:|$)/i
      );
    }
    if (!conclusion) {
      conclusion = response.match(
        /CONCLUSION & RECOMMENDATIONS:[\s\S]*?Conclusion:\s*([\s\S]*?)(?=Recommendations:|$)/i
      );
    }

    // Extract recommendations with multiple patterns
    let recommendations = response.match(
      /\*\*Recommendations:\*\*\s*([\s\S]*?)$/i
    );
    if (!recommendations) {
      recommendations = response.match(/Recommendations:\s*([\s\S]*?)$/i);
    }
    if (!recommendations) {
      recommendations = response.match(
        /CONCLUSION & RECOMMENDATIONS:[\s\S]*?Recommendations:\s*([\s\S]*?)$/i
      );
    }

    data.conclusion = cleanMarkdown(conclusion ? conclusion[1] : null);
    data.recommendations = cleanMarkdown(
      recommendations ? recommendations[1] : null
    );

    // Helper function to extract financial figure with multiple patterns
    const extractFinancialFigure = (response, fieldName) => {
      console.log(`\n=== Extracting ${fieldName} ===`);

      // First, try to find the field in the response
      const fieldPattern = new RegExp(
        `\\*\\*${fieldName}:\\*\\*\\s*([^\\n\\r]+)|${fieldName}:\\s*([^\\n\\r]+)`,
        "i"
      );
      const fieldMatch = response.match(fieldPattern);

      if (!fieldMatch) {
        console.log(`Field "${fieldName}" not found in response`);
        return "N/A";
      }

      const fieldValue = (fieldMatch[1] || fieldMatch[2]).trim();
      console.log(`Raw field value: "${fieldValue}"`);

      // Pattern 1: Handle negative values in parentheses like (£3,730,732)
      const negativeMatch = fieldValue.match(/\(£?([0-9,]+(?:\.[0-9]+)?)\)/);
      if (negativeMatch) {
        const amount = negativeMatch[1];
        console.log(`Found negative value: ${amount}`);
        return `(£${amount})`;
      }

      // Pattern 2: Handle calculations with equals like "£2,876,458 + £2,960,843 - £9,899 = £5,827,402"
      const calculationMatch = fieldValue.match(/=\s*£?([0-9,]+(?:\.[0-9]+)?)/);
      if (calculationMatch) {
        const amount = calculationMatch[1];
        console.log(`Found calculated value: ${amount}`);
        return `£${amount}`;
      }

      // Pattern 3: Handle calculations in parentheses like "£8,200,454 *(£2,913,419 + £5,272,035 - £13,050)*"
      // FIXED: Now actually calculates the expression instead of taking the wrong leading value
      const calcInParensMatch = fieldValue.match(
        /£?([0-9,]+(?:\.[0-9]+)?)\s*\*\(£?([0-9,]+(?:\.[0-9]+)?)\s*\+\s*£?([0-9,]+(?:\.[0-9]+)?)\s*-\s*£?([0-9,]+(?:\.[0-9]+)?)\)\*/
      );
      if (calcInParensMatch) {
        // Ignore the first number (which is wrong) and calculate from the expression
        const wrongValue = calcInParensMatch[1];
        const num1 = parseFloat(calcInParensMatch[2].replace(/,/g, ""));
        const num2 = parseFloat(calcInParensMatch[3].replace(/,/g, ""));
        const num3 = parseFloat(calcInParensMatch[4].replace(/,/g, ""));
        const correctCalculation = num1 + num2 - num3;

        console.log(
          `🔍 Found complex format with INCORRECT leading value: £${wrongValue}`
        );
        console.log(
          `🧮 Calculating expression: ${num1} + ${num2} - ${num3} = ${correctCalculation}`
        );
        console.log(
          `✅ Frontend calculated CORRECT value: £${correctCalculation.toLocaleString()}`
        );

        return `£${correctCalculation.toLocaleString()}`;
      }

      // Pattern 4: Simple positive values like £28,215,224
      const positiveMatch = fieldValue.match(/£?([0-9,]+(?:\.[0-9]+)?)/);
      if (positiveMatch) {
        const amount = positiveMatch[1];
        console.log(`Found positive value: ${amount}`);
        return amount.startsWith("£") ? amount : `£${amount}`;
      }

      // Pattern 5: Handle "N/A" or similar
      if (
        fieldValue.toLowerCase().includes("n/a") ||
        fieldValue.toLowerCase().includes("not available")
      ) {
        console.log(`Found N/A value`);
        return "N/A";
      }

      console.log(`No valid pattern matched for: "${fieldValue}"`);
      return "N/A";
    };

    // Extract current year P&L data
    data.currentYear.turnover = extractFinancialFigure(
      response,
      "Current Year Turnover"
    );
    data.currentYear.costOfSales = extractFinancialFigure(
      response,
      "Current Year Cost of Sales"
    );
    data.currentYear.grossProfit = extractFinancialFigure(
      response,
      "Current Year Gross Profit"
    );
    data.currentYear.operatingExpenses = extractFinancialFigure(
      response,
      "Current Year Operating Expenses"
    );
    data.currentYear.operatingProfit = extractFinancialFigure(
      response,
      "Current Year Operating Profit"
    );
    data.currentYear.financeCosts = extractFinancialFigure(
      response,
      "Current Year Finance Costs"
    );
    data.currentYear.profitAfterTax = extractFinancialFigure(
      response,
      "Current Year Profit After Tax"
    );

    // Extract previous year P&L data
    data.previousYear.turnover = extractFinancialFigure(
      response,
      "Previous Year Turnover"
    );
    data.previousYear.costOfSales = extractFinancialFigure(
      response,
      "Previous Year Cost of Sales"
    );
    data.previousYear.grossProfit = extractFinancialFigure(
      response,
      "Previous Year Gross Profit"
    );
    data.previousYear.operatingExpenses = extractFinancialFigure(
      response,
      "Previous Year Operating Expenses"
    );
    data.previousYear.operatingProfit = extractFinancialFigure(
      response,
      "Previous Year Operating Profit"
    );
    data.previousYear.financeCosts = extractFinancialFigure(
      response,
      "Previous Year Finance Costs"
    );
    data.previousYear.profitAfterTax = extractFinancialFigure(
      response,
      "Previous Year Profit After Tax"
    );

    // Extract balance sheet data - Current Year
    data.balanceSheet.currentYear.fixedAssets = extractFinancialFigure(
      response,
      "Current Year Fixed Assets"
    );
    data.balanceSheet.currentYear.totalStocks = extractFinancialFigure(
      response,
      "Current Year Total Stocks"
    );
    data.balanceSheet.currentYear.totalDebtors = extractFinancialFigure(
      response,
      "Current Year Total Debtors"
    );
    data.balanceSheet.currentYear.totalCash = extractFinancialFigure(
      response,
      "Current Year Total Cash"
    );
    let totalAssetsCY = extractFinancialFigure(
      response,
      "Current Year Total Assets (Fixed Assets + Total Current Assets)"
    );
    if (totalAssetsCY === "N/A") {
      totalAssetsCY = extractFinancialFigure(
        response,
        "Current Year Total Assets"
      );
    }
    data.balanceSheet.currentYear.totalAssets = totalAssetsCY;
    data.balanceSheet.currentYear.tradeCreditors = extractFinancialFigure(
      response,
      "Current Year Trade Creditors"
    );
    data.balanceSheet.currentYear.consignmentStockCreditors =
      extractFinancialFigure(
        response,
        "Current Year Consignment Stock Creditors"
      );
    // Try multiple variations for Amounts Owed to Group Undertakings
    let amountsOwedToGroupCY = extractFinancialFigure(
      response,
      "Current Year Amounts Owed to Group Undertakings"
    );
    if (amountsOwedToGroupCY === "N/A") {
      amountsOwedToGroupCY = extractFinancialFigure(
        response,
        "Current Year Amount Owed to Group Undertaking"
      );
    }
    if (amountsOwedToGroupCY === "N/A") {
      amountsOwedToGroupCY = extractFinancialFigure(
        response,
        "Current Year Amounts Owed by Group Undertakings"
      );
    }
    if (amountsOwedToGroupCY === "N/A") {
      amountsOwedToGroupCY = extractFinancialFigure(
        response,
        "Current Year Amount Owed by Group Undertaking"
      );
    }
    data.balanceSheet.currentYear.amountsOwedToGroup = amountsOwedToGroupCY;
    // Try both singular and plural for Directors' Loan Account
    let directorsLoanAccountCY = extractFinancialFigure(
      response,
      "Current Year Directors' Loan Account"
    );
    if (directorsLoanAccountCY === "N/A") {
      directorsLoanAccountCY = extractFinancialFigure(
        response,
        "Current Year Directors' Loan Accounts"
      );
    }
    data.balanceSheet.currentYear.directorsLoanAccount = directorsLoanAccountCY;

    // Overdraft: Try to extract from the main field, or parse from the full response if not found
    let overdraftCY = extractFinancialFigure(
      response,
      "Current Year Overdraft (if applicable)"
    );
    if (overdraftCY === "N/A") {
      overdraftCY = extractFinancialFigure(
        response,
        "Current Year Overdrafts (if applicable)"
      );
    }
    if (overdraftCY === "N/A") {
      overdraftCY = extractFinancialFigure(response, "Current Year Overdraft");
    }
    if (overdraftCY === "N/A") {
      overdraftCY = extractFinancialFigure(response, "Current Year Overdrafts");
    }
    // Fallback: Try to extract from the full response using a looser regex if still not found
    if (overdraftCY === "N/A") {
      const looseMatch = response.match(
        /Overdraft[^\n\r:]*:?\s*([(\-£0-9,]+)\b/i
      );
      if (looseMatch && looseMatch[1]) {
        overdraftCY = looseMatch[1].trim();
      }
    }
    data.balanceSheet.currentYear.overdraft = overdraftCY;
    data.balanceSheet.currentYear.loans = extractFinancialFigure(
      response,
      "Current Year Loans"
    );
    data.balanceSheet.currentYear.totalLongTermLiability =
      extractFinancialFigure(
        response,
        "Current Year Total Long-Term Liability"
      );
    let currentLiabilitiesCY = extractFinancialFigure(
      response,
      "Current Year Current Liabilities"
    );
    if (currentLiabilitiesCY === "N/A") {
      currentLiabilitiesCY = extractFinancialFigure(
        response,
        "Current Year Current Liabilities (Amounts falling due within one year / Trade and other creditors)"
      );
    }
    if (currentLiabilitiesCY === "N/A") {
      currentLiabilitiesCY = extractFinancialFigure(
        response,
        "Current Year Total Liabilities (amounts falling due within one year)"
      );
    }
    data.balanceSheet.currentYear.currentLiabilities = currentLiabilitiesCY;

    let totalLiabilitiesCY = extractFinancialFigure(
      response,
      "Current Year Total Liabilities (Current Liabilities + Total Long-Term Liability)"
    );
    if (totalLiabilitiesCY === "N/A") {
      totalLiabilitiesCY = extractFinancialFigure(
        response,
        "Current Year Total Liabilities"
      );
    }
    data.balanceSheet.currentYear.totalLiabilities = totalLiabilitiesCY;
    data.balanceSheet.currentYear.netAssets = extractFinancialFigure(
      response,
      "Current Year Net Assets / Shareholders' Funds"
    );

    // Extract balance sheet data - Previous Year
    data.balanceSheet.previousYear.fixedAssets = extractFinancialFigure(
      response,
      "Previous Year Fixed Assets"
    );
    data.balanceSheet.previousYear.totalStocks = extractFinancialFigure(
      response,
      "Previous Year Total Stocks"
    );
    data.balanceSheet.previousYear.totalDebtors = extractFinancialFigure(
      response,
      "Previous Year Total Debtors"
    );
    data.balanceSheet.previousYear.totalCash = extractFinancialFigure(
      response,
      "Previous Year Total Cash"
    );
    let totalAssetsPY = extractFinancialFigure(
      response,
      "Previous Year Total Assets (Fixed Assets + Total Current Assets)"
    );
    if (totalAssetsPY === "N/A") {
      totalAssetsPY = extractFinancialFigure(
        response,
        "Previous Year Total Assets"
      );
    }
    data.balanceSheet.previousYear.totalAssets = totalAssetsPY;
    data.balanceSheet.previousYear.tradeCreditors = extractFinancialFigure(
      response,
      "Previous Year Trade Creditors"
    );
    data.balanceSheet.previousYear.consignmentStockCreditors =
      extractFinancialFigure(
        response,
        "Previous Year Consignment Stock Creditors"
      );
    // Try multiple variations for Amounts Owed to Group Undertakings (Previous Year)
    let amountsOwedToGroupPY = extractFinancialFigure(
      response,
      "Previous Year Amounts Owed to Group Undertakings"
    );
    if (amountsOwedToGroupPY === "N/A") {
      amountsOwedToGroupPY = extractFinancialFigure(
        response,
        "Previous Year Amount Owed to Group Undertaking"
      );
    }
    if (amountsOwedToGroupPY === "N/A") {
      amountsOwedToGroupPY = extractFinancialFigure(
        response,
        "Previous Year Amounts Owed by Group Undertakings"
      );
    }
    if (amountsOwedToGroupPY === "N/A") {
      amountsOwedToGroupPY = extractFinancialFigure(
        response,
        "Previous Year Amount Owed by Group Undertaking"
      );
    }
    data.balanceSheet.previousYear.amountsOwedToGroup = amountsOwedToGroupPY;
    // Try both singular and plural for Directors' Loan Account
    let directorsLoanAccountPY = extractFinancialFigure(
      response,
      "Previous Year Directors' Loan Account"
    );
    if (directorsLoanAccountPY === "N/A") {
      directorsLoanAccountPY = extractFinancialFigure(
        response,
        "Previous Year Directors' Loan Accounts"
      );
    }
    data.balanceSheet.previousYear.directorsLoanAccount =
      directorsLoanAccountPY;

    // Overdraft (Previous Year): Try to extract from the main field, or parse from the full response if not found
    let overdraftPY = extractFinancialFigure(
      response,
      "Previous Year Overdraft (if applicable)"
    );
    if (overdraftPY === "N/A") {
      overdraftPY = extractFinancialFigure(
        response,
        "Previous Year Overdrafts (if applicable)"
      );
    }
    if (overdraftPY === "N/A") {
      overdraftPY = extractFinancialFigure(response, "Previous Year Overdraft");
    }
    if (overdraftPY === "N/A") {
      overdraftPY = extractFinancialFigure(
        response,
        "Previous Year Overdrafts"
      );
    }
    // Fallback: Try to extract from the full response using a looser regex if still not found
    if (overdraftPY === "N/A") {
      const looseMatch = response.match(
        /Overdraft[^\n\r:]*:?\s*([(\-£0-9,]+)\b/i
      );
      if (looseMatch && looseMatch[1]) {
        overdraftPY = looseMatch[1].trim();
      }
    }
    data.balanceSheet.previousYear.overdraft = overdraftPY;
    data.balanceSheet.previousYear.loans = extractFinancialFigure(
      response,
      "Previous Year Loans"
    );
    data.balanceSheet.previousYear.totalLongTermLiability =
      extractFinancialFigure(
        response,
        "Previous Year Total Long-Term Liability"
      );
    let currentLiabilitiesPY = extractFinancialFigure(
      response,
      "Previous Year Current Liabilities"
    );
    if (currentLiabilitiesPY === "N/A") {
      currentLiabilitiesPY = extractFinancialFigure(
        response,
        "Previous Year Current Liabilities (Amounts falling due within one year / Trade and other creditors)"
      );
    }
    if (currentLiabilitiesPY === "N/A") {
      currentLiabilitiesPY = extractFinancialFigure(
        response,
        "Previous Year Total Liabilities (amounts falling due within one year)"
      );
    }
    data.balanceSheet.previousYear.currentLiabilities = currentLiabilitiesPY;

    let totalLiabilitiesPY = extractFinancialFigure(
      response,
      "Previous Year Total Liabilities (Current Liabilities + Total Long-Term Liability)"
    );
    if (totalLiabilitiesPY === "N/A") {
      totalLiabilitiesPY = extractFinancialFigure(
        response,
        "Previous Year Total Liabilities"
      );
    }
    data.balanceSheet.previousYear.totalLiabilities = totalLiabilitiesPY;
    data.balanceSheet.previousYear.netAssets = extractFinancialFigure(
      response,
      "Previous Year Net Assets / Shareholders' Funds"
    );

    const enforceBalanceSheetCalculations = (sheet) => {
      if (!sheet) return;

      const fixedAssets = parseCurrencyToNumber(sheet.fixedAssets);
      const stocks = parseCurrencyToNumber(sheet.totalStocks);
      const debtors = parseCurrencyToNumber(sheet.totalDebtors);
      const cash = parseCurrencyToNumber(sheet.totalCash);

      const hasAllCurrentAssets =
        stocks !== null && debtors !== null && cash !== null;

      if (hasAllCurrentAssets) {
        const currentAssets = stocks + debtors + cash;
        sheet.totalCurrentAssets = formatCurrency(currentAssets);

        if (fixedAssets !== null) {
          const totalAssets = fixedAssets + currentAssets;
          sheet.totalAssets = formatCurrency(totalAssets);
        }
      } else if (!sheet.totalCurrentAssets) {
        sheet.totalCurrentAssets = "N/A";
      }

      const currentLiabilities = parseCurrencyToNumber(
        sheet.currentLiabilities
      );
      const longTermLiabilities = parseCurrencyToNumber(
        sheet.totalLongTermLiability
      );

      sheet.currentLiabilitiesWithinOneYear =
        sheet.currentLiabilities && sheet.currentLiabilities !== ""
          ? sheet.currentLiabilities
          : "N/A";

      if (currentLiabilities !== null && longTermLiabilities !== null) {
        const totalLiabilities = currentLiabilities + longTermLiabilities;
        sheet.totalLiabilities = formatCurrency(totalLiabilities);
        sheet.totalLiabilitiesBreakdown = {
          current: sheet.currentLiabilities,
          longTerm: sheet.totalLongTermLiability,
        };
      }
    };

    enforceBalanceSheetCalculations(data.balanceSheet.currentYear);
    enforceBalanceSheetCalculations(data.balanceSheet.previousYear);

    // Debug logging for section extraction
    console.log("=== SECTION EXTRACTION DEBUG ===");
    console.log(
      "Balance Sheet Analysis found:",
      data.balanceSheetAnalysis !== "N/A"
    );
    console.log("Conclusion found:", data.conclusion !== "N/A");
    console.log("Recommendations found:", data.recommendations !== "N/A");
    if (
      data.balanceSheetAnalysis === "N/A" ||
      data.conclusion === "N/A" ||
      data.recommendations === "N/A"
    ) {
      console.log("Raw AI response (last 2000 chars):", response.slice(-2000));
    }
    console.log("Parsed comprehensive financial data:", data); // Debug log to see parsed data

    return data;
  };

  const downloadReport = () => {
    if (!reportData) return;

    const doc = new jsPDF();
    const pageHeight = doc.internal.pageSize.height;
    let yPosition = 20;

    // Add title
    doc.setFontSize(18);
    doc.setFont(undefined, "bold");
    doc.text("Comprehensive Financial Report Analysis", 20, yPosition);
    yPosition += 20;

    // Add generated date
    doc.setFontSize(12);
    doc.setFont(undefined, "normal");
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, yPosition);
    yPosition += 20;

    // Section 1 - Company Information
    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text("Section 1 - Company Information", 20, yPosition);
    yPosition += 15;

    // Helper function to add company info section
    const addInfoSection = (title, content) => {
      if (yPosition > pageHeight - 20) {
        doc.addPage();
        yPosition = 20;
      }
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text(title, 20, yPosition);
      yPosition += 8;

      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      const lines = doc.splitTextToSize(content, 170);
      doc.text(lines, 20, yPosition);
      yPosition += lines.length * 6 + 5;
    };

    // Add all company information sections
    addInfoSection(
      "1. Company Name",
      reportData.companyInfo?.companyName || "N/A"
    );
    addInfoSection(
      "2. Main Activities",
      reportData.companyInfo?.mainActivities || "N/A"
    );
    addInfoSection("3. Brands", reportData.companyInfo?.brands || "N/A");
    addInfoSection("4. Directors", reportData.companyInfo?.directors || "N/A");
    addInfoSection(
      "5. Market Conditions",
      reportData.companyInfo?.marketConditions || "N/A"
    );
    addInfoSection(
      "6. Business Overview",
      reportData.companyInfo?.businessOverview || "N/A"
    );
    addInfoSection(
      "7. Exceptions",
      reportData.companyInfo?.exceptions || "N/A"
    );
    addInfoSection(
      "8. Auditor's Opinion",
      reportData.companyInfo?.auditorOpinion || "N/A"
    );
    addInfoSection(
      "9. Going Concern",
      reportData.companyInfo?.goingConcern || "N/A"
    );

    yPosition += 10;

    // Section 2 - Financial Data Section
    if (yPosition > pageHeight - 50) {
      doc.addPage();
      yPosition = 20;
    }
    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text("Section 2 - Profit & Loss Analysis", 20, yPosition);
    yPosition += 15;

    // Create comprehensive financial table
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text("Table of Key P&L Financial Figures", 20, yPosition);
    yPosition += 15;

    // Table headers
    doc.setFontSize(9);
    doc.setFont(undefined, "bold");
    doc.text("Item", 20, yPosition);
    doc.text("Current Year", 70, yPosition);
    doc.text("Previous Year", 115, yPosition);
    doc.text("Change Amount", 155, yPosition);
    doc.text("Change %", 185, yPosition);
    yPosition += 8;

    // Draw header line
    doc.line(15, yPosition - 2, 200, yPosition - 2);
    yPosition += 5;

    // Helper function to extract amount from text
    const extractAmount = (text, item = "") => {
      if (!text || text === "N/A") return "N/A";
      let str = text.toString();

      // For specific fields, always show negative values with minus sign, not parentheses
      const forceMinusFields = [
        "Operating Profit",
        "Profit After Tax",
        "Directors' Loan Accounts",
        "Total Long-Term Liability",
        "Total Liabilities",
      ];

      // Remove parentheses and use minus for these fields
      if (forceMinusFields.includes(item)) {
        // Handle negative in parentheses: (£3,730,732)
        const negParens = str.match(/\(£?([0-9,]+(?:\.[0-9]+)?)\)/);
        if (negParens) {
          return `-£${negParens[1]}`;
        }
        // Handle negative with minus: -£3,730,732
        if (str.startsWith("-£")) {
          return str;
        }
        // Handle positive
        const amt = str.match(/£?([0-9,]+(?:\.[0-9]+)?)/);
        if (amt) return `£${amt[1]}`;
        return str;
      }

      // Default: keep original logic for other fields
      // Handle negative values in parentheses format: (£3,730,732)
      const negativeMatch = str.match(/\(£?([0-9,]+(?:\.[0-9]+)?)\)/);
      if (negativeMatch) {
        return `(£${negativeMatch[1]})`;
      }
      // Handle negative values with minus sign: -£3,730,732
      if (str.startsWith("-£")) {
        const amount = str.substring(2);
        return `(£${amount})`;
      }
      // Extract positive amounts
      const amountMatch = str.match(/£?([0-9,]+(?:\.[0-9]+)?)/);
      if (amountMatch) {
        const amount = amountMatch[1];
        return `£${amount}`;
      }
      return str;
    };

    // Helper function to calculate change amount
    const calculateChangeAmount = (current, previous) => {
      // Handle negative values and clean the text
      const cleanValue = (val) => {
        if (!val || val === "N/A") return NaN;
        let cleanVal = val.toString();

        // Handle negative values in parentheses: (£3,730,732)
        const negativeMatch = cleanVal.match(/\(£?([0-9,]+(?:\.[0-9]+)?)\)/);
        if (negativeMatch) {
          return -parseFloat(negativeMatch[1].replace(/,/g, ""));
        }

        // Handle negative values with minus: -£3,730,732
        if (cleanVal.startsWith("-")) {
          return -parseFloat(cleanVal.replace(/[£,-]/g, ""));
        }

        // Handle positive values: £3,730,732
        return parseFloat(cleanVal.replace(/[£,]/g, ""));
      };

      const curr = cleanValue(current);
      const prev = cleanValue(previous);

      if (isNaN(curr) || isNaN(prev)) return "N/A";

      const changeAmount = curr - prev;
      const absChange = Math.abs(changeAmount);

      if (changeAmount >= 0) {
        return `£${absChange.toLocaleString()}`;
      } else {
        return `-£${absChange.toLocaleString()}`;
      }
    };

    // Helper function to calculate percentage change
    const calculateChangePercentage = (current, previous) => {
      // Handle negative values and clean the text - reuse the same logic
      const cleanValue = (val) => {
        if (!val || val === "N/A") return NaN;
        let cleanVal = val.toString();

        // Handle negative values in parentheses: (£3,730,732)
        const negativeMatch = cleanVal.match(/\(£?([0-9,]+(?:\.[0-9]+)?)\)/);
        if (negativeMatch) {
          return -parseFloat(negativeMatch[1].replace(/,/g, ""));
        }

        // Handle negative values with minus: -£3,730,732
        if (cleanVal.startsWith("-")) {
          return -parseFloat(cleanVal.replace(/[£,-]/g, ""));
        }

        // Handle positive values: £3,730,732
        return parseFloat(cleanVal.replace(/[£,]/g, ""));
      };

      const curr = cleanValue(current);
      const prev = cleanValue(previous);

      if (isNaN(curr) || isNaN(prev) || prev === 0) return "N/A";

      const change = (((curr - prev) / Math.abs(prev)) * 100).toFixed(1);
      return `${change}%`;
    };

    // Process the P&L data structure into table rows
    const tableRows = [
      [
        "Turnover",
        extractAmount(reportData.currentYear.turnover, "Turnover"),
        extractAmount(reportData.previousYear.turnover, "Turnover"),
      ],
      [
        "Cost of Sales",
        extractAmount(reportData.currentYear.costOfSales, "Cost of Sales"),
        extractAmount(reportData.previousYear.costOfSales, "Cost of Sales"),
      ],
      [
        "Gross Profit",
        extractAmount(reportData.currentYear.grossProfit, "Gross Profit"),
        extractAmount(reportData.previousYear.grossProfit, "Gross Profit"),
      ],
      [
        "Operating Expenses",
        extractAmount(
          reportData.currentYear.operatingExpenses,
          "Operating Expenses"
        ),
        extractAmount(
          reportData.previousYear.operatingExpenses,
          "Operating Expenses"
        ),
      ],
      [
        "Operating Profit",
        extractAmount(
          reportData.currentYear.operatingProfit,
          "Operating Profit"
        ),
        extractAmount(
          reportData.previousYear.operatingProfit,
          "Operating Profit"
        ),
      ],
      [
        "Finance Costs",
        extractAmount(reportData.currentYear.financeCosts, "Finance Costs"),
        extractAmount(reportData.previousYear.financeCosts, "Finance Costs"),
      ],
      [
        "Profit After Tax",
        extractAmount(
          reportData.currentYear.profitAfterTax,
          "Profit After Tax"
        ),
        extractAmount(
          reportData.previousYear.profitAfterTax,
          "Profit After Tax"
        ),
      ],
    ];

    doc.setFont(undefined, "normal");
    tableRows.forEach((row) => {
      if (yPosition > pageHeight - 15) {
        doc.addPage();
        yPosition = 20;
      }

      const [item, current, previous] = row;
      const changeAmount = calculateChangeAmount(current, previous);
      const changePercentage = calculateChangePercentage(current, previous);

      doc.text(item, 20, yPosition);
      doc.text(current, 70, yPosition);
      doc.text(previous, 115, yPosition);
      doc.text(changeAmount, 155, yPosition);
      doc.text(changePercentage, 185, yPosition);
      yPosition += 8;
    });

    // Add Detailed Financial Explanation for P&L
    yPosition += 10;
    if (yPosition > pageHeight - 50) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text("Detailed P&L Financial Explanation", 20, yPosition);
    yPosition += 15;

    // Paragraph 1
    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.text("Reasons for Financial Changes:", 20, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    if (
      reportData.financialExplanation?.paragraph1 &&
      reportData.financialExplanation.paragraph1 !== "N/A"
    ) {
      const paragraph1Lines = doc.splitTextToSize(
        reportData.financialExplanation.paragraph1,
        170
      );
      paragraph1Lines.forEach((line) => {
        if (yPosition > pageHeight - 15) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, 20, yPosition);
        yPosition += 6;
      });
    } else {
      doc.text(
        "No detailed explanation available for financial changes.",
        20,
        yPosition
      );
      yPosition += 6;
    }

    yPosition += 5;

    // Paragraph 2
    if (yPosition > pageHeight - 20) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.text("Market and Industry Analysis:", 20, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    if (
      reportData.financialExplanation?.paragraph2 &&
      reportData.financialExplanation.paragraph2 !== "N/A"
    ) {
      const paragraph2Lines = doc.splitTextToSize(
        reportData.financialExplanation.paragraph2,
        170
      );
      paragraph2Lines.forEach((line) => {
        if (yPosition > pageHeight - 15) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, 20, yPosition);
        yPosition += 6;
      });
    } else {
      doc.text("No market analysis available.", 20, yPosition);
      yPosition += 6;
    }

    // Section 3 - Balance Sheet Analysis
    yPosition += 15;
    if (yPosition > pageHeight - 50) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text("Section 3 - Balance Sheet Analysis", 20, yPosition);
    yPosition += 15;

    // Balance Sheet table
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text("Table of Balance Sheet Figures", 20, yPosition);
    yPosition += 15;

    // Balance Sheet table headers
    doc.setFontSize(9);
    doc.setFont(undefined, "bold");
    doc.text("Item", 20, yPosition);
    doc.text("Current Year", 70, yPosition);
    doc.text("Previous Year", 115, yPosition);
    doc.text("Change Amount", 155, yPosition);
    doc.text("Change %", 185, yPosition);
    yPosition += 8;

    // Draw header line
    doc.line(15, yPosition - 2, 200, yPosition - 2);
    yPosition += 5;

    // Balance sheet table rows
    const balanceSheetRows = [
      [
        "Fixed Assets",
        extractAmount(
          reportData.balanceSheet?.currentYear?.fixedAssets,
          "Fixed Assets"
        ),
        extractAmount(
          reportData.balanceSheet?.previousYear?.fixedAssets,
          "Fixed Assets"
        ),
      ],
      [
        "Total Stocks",
        extractAmount(
          reportData.balanceSheet?.currentYear?.totalStocks,
          "Total Stocks"
        ),
        extractAmount(
          reportData.balanceSheet?.previousYear?.totalStocks,
          "Total Stocks"
        ),
      ],
      [
        "Total Debtors",
        extractAmount(
          reportData.balanceSheet?.currentYear?.totalDebtors,
          "Total Debtors"
        ),
        extractAmount(
          reportData.balanceSheet?.previousYear?.totalDebtors,
          "Total Debtors"
        ),
      ],
      [
        "Total Cash",
        extractAmount(
          reportData.balanceSheet?.currentYear?.totalCash,
          "Total Cash"
        ),
        extractAmount(
          reportData.balanceSheet?.previousYear?.totalCash,
          "Total Cash"
        ),
      ],
      [
        "Total Current Assets",
        extractAmount(
          reportData.balanceSheet?.currentYear?.totalCurrentAssets,
          "Total Current Assets"
        ),
        extractAmount(
          reportData.balanceSheet?.previousYear?.totalCurrentAssets,
          "Total Current Assets"
        ),
      ],
      [
        "Total Assets",
        extractAmount(
          reportData.balanceSheet?.currentYear?.totalAssets,
          "Total Assets"
        ),
        extractAmount(
          reportData.balanceSheet?.previousYear?.totalAssets,
          "Total Assets"
        ),
      ],
      [
        "Trade Creditors",
        extractAmount(
          reportData.balanceSheet?.currentYear?.tradeCreditors,
          "Trade Creditors"
        ),
        extractAmount(
          reportData.balanceSheet?.previousYear?.tradeCreditors,
          "Trade Creditors"
        ),
      ],
      [
        "Consign. Stock Creditors",
        extractAmount(
          reportData.balanceSheet?.currentYear?.consignmentStockCreditors,
          "Consign. Stock Creditors"
        ),
        extractAmount(
          reportData.balanceSheet?.previousYear?.consignmentStockCreditors,
          "Consign. Stock Creditors"
        ),
      ],
      [
        "Group Undertakings",
        extractAmount(
          reportData.balanceSheet?.currentYear?.amountsOwedToGroup,
          "Group Undertakings"
        ),
        extractAmount(
          reportData.balanceSheet?.previousYear?.amountsOwedToGroup,
          "Group Undertakings"
        ),
      ],
      [
        "Directors' Loan Accounts",
        extractAmount(
          reportData.balanceSheet?.currentYear?.directorsLoanAccount,
          "Directors' Loan Accounts"
        ),
        extractAmount(
          reportData.balanceSheet?.previousYear?.directorsLoanAccount,
          "Directors' Loan Accounts"
        ),
      ],
      [
        "Overdraft",
        extractAmount(
          reportData.balanceSheet?.currentYear?.overdraft,
          "Overdraft"
        ),
        extractAmount(
          reportData.balanceSheet?.previousYear?.overdraft,
          "Overdraft"
        ),
      ],
      [
        "Loans",
        extractAmount(reportData.balanceSheet?.currentYear?.loans, "Loans"),
        extractAmount(reportData.balanceSheet?.previousYear?.loans, "Loans"),
      ],
      [
        "Current Liabilities (within one year)",
        extractAmount(
          reportData.balanceSheet?.currentYear
            ?.currentLiabilitiesWithinOneYear ||
            reportData.balanceSheet?.currentYear?.currentLiabilities,
          "Current Liabilities"
        ),
        extractAmount(
          reportData.balanceSheet?.previousYear
            ?.currentLiabilitiesWithinOneYear ||
            reportData.balanceSheet?.previousYear?.currentLiabilities,
          "Current Liabilities"
        ),
      ],
      [
        "Total Long-Term Liability",
        extractAmount(
          reportData.balanceSheet?.currentYear?.totalLongTermLiability,
          "Total Long-Term Liability"
        ),
        extractAmount(
          reportData.balanceSheet?.previousYear?.totalLongTermLiability,
          "Total Long-Term Liability"
        ),
      ],
      [
        "Total Liabilities",
        extractAmount(
          reportData.balanceSheet?.currentYear?.totalLiabilities,
          "Total Liabilities"
        ),
        extractAmount(
          reportData.balanceSheet?.previousYear?.totalLiabilities,
          "Total Liabilities"
        ),
      ],
      [
        "Net Assets / Shareholders' Funds",
        extractAmount(
          reportData.balanceSheet?.currentYear?.netAssets,
          "Net Assets / Shareholders' Funds"
        ),
        extractAmount(
          reportData.balanceSheet?.previousYear?.netAssets,
          "Net Assets / Shareholders' Funds"
        ),
      ],
    ];

    doc.setFont(undefined, "normal");
    balanceSheetRows.forEach((row) => {
      if (yPosition > pageHeight - 15) {
        doc.addPage();
        yPosition = 20;
      }

      const [item, current, previous] = row;
      const changeAmount = calculateChangeAmount(current, previous);
      const changePercentage = calculateChangePercentage(current, previous);

      doc.text(item, 20, yPosition);
      doc.text(current || "N/A", 70, yPosition);
      doc.text(previous || "N/A", 115, yPosition);
      doc.text(changeAmount, 155, yPosition);
      doc.text(changePercentage, 185, yPosition);
      yPosition += 8;
    });

    // Balance Sheet Analysis
    yPosition += 10;
    if (yPosition > pageHeight - 50) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text("Balance Sheet Analysis", 20, yPosition);
    yPosition += 15;

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    if (
      reportData.balanceSheetAnalysis &&
      reportData.balanceSheetAnalysis !== "N/A"
    ) {
      const balanceSheetLines = doc.splitTextToSize(
        reportData.balanceSheetAnalysis,
        170
      );
      balanceSheetLines.forEach((line) => {
        if (yPosition > pageHeight - 15) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, 20, yPosition);
        yPosition += 6;
      });
    } else {
      doc.text("No balance sheet analysis available.", 20, yPosition);
      yPosition += 6;
    }

    // Conclusion & Recommendations
    yPosition += 15;
    if (yPosition > pageHeight - 50) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text("Conclusion & Recommendations", 20, yPosition);
    yPosition += 15;

    // Conclusion
    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.text("Conclusion:", 20, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    if (reportData.conclusion && reportData.conclusion !== "N/A") {
      const conclusionLines = doc.splitTextToSize(reportData.conclusion, 170);
      conclusionLines.forEach((line) => {
        if (yPosition > pageHeight - 15) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, 20, yPosition);
        yPosition += 6;
      });
    } else {
      doc.text("No conclusion available.", 20, yPosition);
      yPosition += 6;
    }

    yPosition += 8;

    // Recommendations
    if (yPosition > pageHeight - 30) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.text("Recommendations:", 20, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    if (reportData.recommendations && reportData.recommendations !== "N/A") {
      const recommendationsLines = doc.splitTextToSize(
        reportData.recommendations,
        170
      );
      recommendationsLines.forEach((line) => {
        if (yPosition > pageHeight - 15) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(line, 20, yPosition);
        yPosition += 6;
      });
    } else {
      doc.text("No recommendations available.", 20, yPosition);
      yPosition += 6;
    }

    // Save the PDF
    // Use company name for file name, fallback to generic if missing
    let companyNameRaw = reportData?.companyInfo?.companyName || "Company";
    // Remove reporting period and extra context (e.g., 'for the year ended ...')
    let companyName = companyNameRaw.split(
      /for the year|for the period|for the financial year|for the quarter|for the month/i
    )[0];
    companyName = companyName
      .replace(/[^a-zA-Z0-9 ]/g, "")
      .replace(/\s+/g, " ")
      .trim();
    if (!companyName) companyName = "Company";
    const fileName = `${companyName} Financial Report.pdf`;
    doc.save(fileName);
  };

  const sendQuestion = async () => {
    if (!question.trim() || loading.question) return;

    const userMessage = { type: "user", text: question };
    setMessages((prev) => [...prev, userMessage]);
    setQuestion("");
    setLoading((prev) => ({ ...prev, question: true }));

    try {
      const res = await axios.post(`${getApiUrl()}/ask`, { question });
      const aiMessage = { type: "ai", text: res.data.answer };
      setMessages((prev) => [...prev, aiMessage]);
    } catch (err) {
      const errorMsg = {
        type: "ai",
        text: "Sorry, I encountered an error. Please try again later.",
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading((prev) => ({ ...prev, question: false }));
    }
  };

  const addMessage = (text, type = "ai") => {
    setMessages((prev) => [...prev, { type, text }]);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
    } else {
      addMessage("Please upload a valid PDF file", "error");
    }
  };

  const resetFile = () => {
    setFile(null);
    setPdfUploaded(false);
    setMessages([]);
    setReportGenerated(false);
    setReportData(null);
    setReportProgress("");
    // Create a new file input element to reset it
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleNewFile = () => {
    resetFile();
  };

  return (
    <Router>
      <div className="app-container">
        {/* Hidden file input that's always present */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".pdf"
          className="hidden-input"
        />

        <div className="app-header">
          <div className="logo">
            <BsRobot className="robot-icon" />
            <h1>Neura: PDF Assistant</h1>
          </div>
          <p className="subtitle">
            Upload a text-based PDF and ask anything about its content
          </p>
          <p className="pdf-requirements">
            📄 Supports text-based PDFs only • ❌ Image/scanned PDFs not
            supported
          </p>
        </div>

        <div className="main-content">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route
              path="/upload"
              element={
                !pdfUploaded ? (
                  <div className="upload-container">
                    <div
                      className="upload-area"
                      onClick={() => fileInputRef.current.click()}
                    >
                      {file ? (
                        <div className="file-preview">
                          <FiFile className="file-icon" />
                          <span>{file.name}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              resetFile();
                            }}
                            className="clear-file"
                          >
                            <FiX />
                          </button>
                        </div>
                      ) : (
                        <>
                          <FiUpload className="upload-icon" />
                          <p>Click to upload PDF</p>
                          <small className="upload-hint">
                            Only text-based PDFs supported
                          </small>
                        </>
                      )}
                    </div>

                    {file && (
                      <button
                        onClick={handleUpload}
                        disabled={loading.upload}
                        className="upload-button"
                      >
                        {loading.upload ? (
                          <FiLoader className="spin" />
                        ) : (
                          "Process PDF"
                        )}
                      </button>
                    )}
                    <Link to="/" className="back-to-home">
                      Back to Home
                    </Link>
                  </div>
                ) : (
                  <div className="chat-container">
                    <div className="chat-header">
                      <button
                        onClick={handleNewFile}
                        className="new-file-button"
                      >
                        <BsArrowLeft /> New File
                      </button>

                      <div className="header-actions">
                        {!reportGenerated && !loading.report && (
                          <button
                            onClick={generateReport}
                            className="generate-report-button"
                          >
                            <BsFileEarmarkText /> Generate Report
                          </button>
                        )}

                        {loading.report && (
                          <div className="report-progress">
                            <FiLoader className="spin" />
                            <span>{reportProgress}</span>
                          </div>
                        )}

                        {reportGenerated && (
                          <button
                            onClick={downloadReport}
                            className="download-button"
                          >
                            <FiDownload /> Download Report
                          </button>
                        )}
                      </div>

                      <div className="current-file">
                        <FiFile /> {file.name}
                      </div>
                    </div>

                    <div className="chat-messages">
                      {messages.map((msg, idx) => (
                        <div
                          key={idx}
                          className={`message ${msg.type} ${
                            msg.type === "error"
                              ? "error"
                              : msg.type === "warning"
                              ? "warning"
                              : ""
                          }`}
                        >
                          <div className="message-header">
                            {msg.type === "user" ? (
                              <BsPerson className="icon" />
                            ) : msg.type === "ai" ? (
                              <BsRobot className="icon" />
                            ) : (
                              <FiFile className="icon" />
                            )}
                            <span className="sender">
                              {msg.type === "user"
                                ? "You"
                                : msg.type === "ai"
                                ? "Neura"
                                : "System"}
                            </span>
                          </div>
                          <div className="message-content">{msg.text}</div>
                        </div>
                      ))}
                      {loading.question && (
                        <div className="message ai thinking">
                          <div className="message-header">
                            <BsRobot className="icon" />
                            <span className="sender">Neura</span>
                          </div>
                          <div className="message-content">
                            <div className="typing-indicator">
                              <span></span>
                              <span></span>
                              <span></span>
                            </div>
                          </div>
                        </div>
                      )}
                      <div ref={chatEndRef} />
                    </div>

                    <div className="chat-input">
                      <div className="input-wrapper">
                        <MdOutlineAttachFile
                          className="attach-icon"
                          onClick={() => fileInputRef.current.click()}
                        />
                        <input
                          type="text"
                          placeholder="Ask a question about your PDF..."
                          value={question}
                          onChange={(e) => setQuestion(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && sendQuestion()}
                          disabled={loading.question}
                        />
                        <button
                          onClick={sendQuestion}
                          disabled={loading.question || !question.trim()}
                          className="send-button"
                        >
                          {loading.question ? (
                            <FiLoader className="spin" />
                          ) : (
                            <FiSend />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )
              }
            />
          </Routes>
        </div>
      </div>
    </Router>
  );
}

export default App;
